import { Type } from '@sinclair/typebox';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import styles from '../../../../LotteryForm.module.scss';
import { ControlledInputField } from '../../../../components/controlled-input-field/ControlledInputField';
import { useFormModule } from '../../../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../../../types';

export type SwipePeacefulFormType = {
  amountOfQuestions: string;
};

export const SwipePeacefulFormTypeSchema = Type.Object(
  {
    amountOfQuestions: Type.Integer({
      minimum: 3,
      maximum: 30,
      errorMessage: {
        type: 'form-error-message-numeric-values',
        minimum: 'form-error-message-minimum-numeric-value',
        maximum: 'form-error-message-maximum-numeric-value',
      },
    }),
  },
  { additionalProperties: true, $id: 'SwipePeacefulFormSchema' }
);

export const SwipePeacefulFormTransformer: ModuleTransformer<SwipePeacefulFormType> = {
  fromPayload: ({ lottery }) => {
    if (lottery.swipe?.config?.modeData.mode === 'PEACEFUL') {
      return {
        amountOfQuestions: '' + (lottery.swipe?.config?.modeData.amountOfQuestions ?? 0),
      };
    } else {
      return {
        amountOfQuestions: '',
      };
    }
  },
  toPayload: (formData) => {
    return {
      lottery: {
        swipe: {
          config: {
            modeData: {
              amountOfQuestions: parseInt(formData.amountOfQuestions, 10),
            },
          },
        },
      },
    };
  },
};

export type SwipePeacefulFormProps = FormModuleProps<SwipePeacefulFormType> & {
  moduleName: string;
  setRequiredNumberOfQuestions: (requiredNumberOfQuestions: number) => void;
};

export const SwipePeacefulForm: React.FC<SwipePeacefulFormProps> = ({
  moduleName,
  values,
  onChange,
  onValidation,
  setRequiredNumberOfQuestions,
}) => {
  const { control } = useFormModule({
    moduleName,
    defaultValues: values,
    schema: SwipePeacefulFormTypeSchema,
    onChange,
    onValidation,
  });

  //Tests if the amount of questions is smaller than actual amount of questions
  const amountOfQuestions = useWatch({
    control,
    name: 'amountOfQuestions',
  });

  useEffect(() => {
    onValidation(moduleName, false);
  }, [onValidation, moduleName]);

  useEffect(() => {
    const test = parseInt(amountOfQuestions, 10);
    setRequiredNumberOfQuestions(test);
  }, [amountOfQuestions, setRequiredNumberOfQuestions]);

  return (
    <div className={styles['form-elements']}>
      <ControlledInputField
        control={control}
        labelKey="form-sign-up-swipe-config-amount-of-questions-label"
        name="amountOfQuestions"
        type="number"
        fieldHelper={{
          msg: `${SwipePeacefulFormTypeSchema.properties.amountOfQuestions.minimum}-${SwipePeacefulFormTypeSchema.properties.amountOfQuestions.maximum}`,
        }}
        showCharacterLimit={false}
        upperLimit={SwipePeacefulFormTypeSchema.properties.amountOfQuestions.maximum}
        lowerLimit={SwipePeacefulFormTypeSchema.properties.amountOfQuestions.minimum}
      />
    </div>
  );
};
